/**
 * Класс для наблюдения за секциями и добавления классов анимации
 * при их появлении в области видимости
 *
 * Использует IntersectionObserver API с фолбэком для старых браузеров
 * @class
 */
class SectionObserver {
  /**
   * Создает экземпляр SectionObserver
   * @constructor
   */
  constructor() {
    /**
     * Настройки наблюдателя
     * @type {Object}
     * @property {Element|null} root - Элемент, используемый как viewport
     * @property {string} rootMargin - Отступы от root
     * @property {number} threshold - Порог видимости для срабатывания (0-1)
     * @property {string} sectionClass - CSS-класс для отслеживаемых секций
     * @property {string} animateClass - CSS-класс для анимации
     * @property {boolean} once - Анимировать только один раз
     */
    this.options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
      sectionClass: 'js-section',
      animateClass: 'animate',
      once: true,
    };

    /**
     * Экземпляр IntersectionObserver
     * @type {IntersectionObserver|null}
     */
    this.observer = null;

    // Инициализация
    this.init();
  }

  /**
   * Инициализирует наблюдатель
   * @returns {void}
   */
  init() {
    // Проверяем поддержку IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      this.fallbackAnimation();
      return;
    }

    // Создаем наблюдатель
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
      root: this.options.root,
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold,
    });

    // Находим все секции и начинаем наблюдение
    const sections = document.querySelectorAll(`.${this.options.sectionClass}`);
    sections.forEach((section) => {
      this.observer.observe(section);
    });
  }

  /**
   * Обрабатывает пересечения элементов с viewport
   * @param {IntersectionObserverEntry[]} entries - Массив записей о пересечении
   * @returns {void}
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      const section = entry.target;

      if (entry.isIntersecting) {
        // Добавляем класс анимации
        section.classList.add(this.options.animateClass);

        // Если нужно анимировать только один раз, прекращаем наблюдение
        if (this.options.once) {
          this.observer.unobserve(section);
        }
      } else if (!this.options.once) {
        // Если анимация должна повторяться при каждом входе/выходе
        section.classList.remove(this.options.animateClass);
      }
    });
  }

  /**
   * Фолбэк для браузеров без поддержки IntersectionObserver
   * Использует события загрузки и скролла для проверки видимости
   * @returns {void}
   */
  fallbackAnimation() {
    const sections = document.querySelectorAll(`.${this.options.sectionClass}`);

    /**
     * Проверяет видимость секций
     * @returns {void}
     */
    const checkVisibility = () => {
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight * (1 - this.options.threshold) && rect.bottom >= 0;

        if (isVisible) {
          section.classList.add(this.options.animateClass);
        }
      });
    };

    // Проверяем при загрузке и скролле
    window.addEventListener('load', checkVisibility);
    window.addEventListener('scroll', checkVisibility);

    // Первоначальная проверка
    checkVisibility();
  }
}

// Создаем экземпляр наблюдателя
new SectionObserver();
